import { createContext, useState, useEffect, useContext } from 'react';
import { authApi } from '../services/api.service';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// Check if we're in development mode with auth bypass enabled
const isBypassAuthEnabled = import.meta.env.VITE_BYPASS_AUTH === 'true';

export function AuthProvider({ children }) {
  // In development with bypass enabled, use a mock user
  const initialUser = isBypassAuthEnabled ? {
    id: 'dev-user',
    email: 'eric@aiappbuilder.io', // Use your email for PM demo access
    name: 'Eric (Dev Mode)',
    roles: ['admin', 'manager', 'user'] // Include all roles for development
  } : null;

  const [user, setUser] = useState(initialUser);
  const [loading, setLoading] = useState(!isBypassAuthEnabled);
  const [error, setError] = useState(null);

  // Initial authentication check
  useEffect(() => {
    // Skip auth check if bypass is enabled
    if (isBypassAuthEnabled) {
      console.log('Development mode: Using mock authentication');
      setLoading(false);
      return;
    }
    
    // Check if user is already logged in
    const checkAuth = async () => {
      try {
        const response = await authApi.checkAuthStatus();
        if (response.data.authenticated) {
          const userData = response.data.user;
          
          // TEMPORARY: Add admin role for specific users to enable admin access
          // This is a temporary solution to allow admin access for eric@exineducation.com and eric@aiappbuilder.io
          if (userData.email === 'eric@exineducation.com' || userData.email === 'eric@aiappbuilder.io') {
            console.log(`Adding admin role to ${userData.email}`);
            userData.roles = userData.roles || [];
            if (!userData.roles.includes('admin')) {
              userData.roles.push('admin');
            }
          }
          
          setUser(userData);
        }
      } catch (err) {
        console.error('Auth check failed:', err);
        // Don't set error for initial auth check - user might just not be logged in
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async () => {
    try {
      // Redirect to Google OAuth login
      const loginUrl = authApi.getLoginUrl();
      console.log('Attempting to redirect to:', loginUrl);
      console.log('Current location:', window.location.href);
      
      // Use replace to prevent back button issues
      window.location.replace(loginUrl);
      
      // Add a small delay to ensure redirect happens
      await new Promise(resolve => setTimeout(resolve, 100));
    } catch (err) {
      console.error('Login error:', err);
      setError('An error occurred during login. Please try again.');
      throw err;
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
      // Clear any stored CSRF token
      localStorage.removeItem('csrfToken');
      setUser(null);
      
      // Force redirect to login page after logout
      window.location.replace('/login');
    } catch (err) {
      console.error('Logout failed:', err);
      // Even if logout fails on server, clear local state
      localStorage.removeItem('csrfToken');
      setUser(null);
      window.location.replace('/login');
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    clearError,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}